import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReferralTracking } from '@/contexts/ReferralTrackingContext';
import { useSecureReferralToken } from './useSecureReferralToken';
import { useReferralClickTracking } from './useReferralClickTracking';

// localStorage key for tracking failed tokens
const FAILED_TOKENS_KEY = 'evee_failed_referral_tokens';
const MAX_RETRY_ATTEMPTS = 1; // Only try once per token

// sessionStorage keys for per-session deduplication (StrictMode + multi-instance safe)
const IN_PROGRESS_KEY = 'evee_referral_in_progress';
const PROCESSED_KEY = 'evee_referral_processed';

interface FailedTokenRecord {
  token: string;
  attemptCount: number;
  lastAttempt: string;
  reason: string;
}

/**
 * Hook to handle token-based referral tracking from URL parameters
 * Automatically validates tokens and stores referral data
 * 
 * FIXED: Prevents infinite loops by:
 * 1. Using useRef for validation state (persists across renders)
 * 2. Memoized validateToken function
 * 3. Tracking which specific token was validated
 */
export function useTokenReferralTracking() {
  const [searchParams] = useSearchParams();
  const { setReferralData, referralData } = useReferralTracking();
  const { validateToken } = useSecureReferralToken();
  
  // Support both old (ref_token) and new (t) query params
  const refToken = searchParams.get('t') || searchParams.get('ref_token');
  
  // Track clicks on referral tokens
  useReferralClickTracking(refToken || undefined);

  // Use ref to persist validation state across renders
  const validatedTokenRef = useRef<string | null>(null);
  const isValidatingRef = useRef(false);

  useEffect(() => {
    // Early exit: No token in URL
    if (!refToken) {
      // Check for traditional URL params as fallback
      const recruiterIdParam = searchParams.get('recruiter_id');
      const refParam = searchParams.get('ref');
      
      if (recruiterIdParam && refParam === 'recruiter') {
        // Traditional URL-based referral tracking (backward compatibility)
        const now = new Date();
        const expiresAt = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000)); // 90 days
        
        setReferralData({
          recruiter_id: recruiterIdParam,
          referral_source: 'url_params',
          timestamp: now.toISOString(),
          expires_at: expiresAt.toISOString(),
        });
      }
      return;
    }

    // Early exit: Already validated this specific token (component-level guard)
    if (validatedTokenRef.current === refToken) {
      console.log('⏭️ [useTokenReferralTracking] Already validated in this instance:', refToken.substring(0, 8) + '...');
      return;
    }

    // Early exit: Token already processed in this session (sessionStorage guard)
    const processedTokens = getProcessedTokens();
    if (processedTokens.includes(refToken)) {
      console.log('✅ [useTokenReferralTracking] Token already processed this session:', refToken.substring(0, 8) + '...');
      validatedTokenRef.current = refToken; // Mark locally as well
      return;
    }

    // Early exit: Token is currently being validated (sessionStorage guard for multi-instance)
    const inProgressToken = sessionStorage.getItem(IN_PROGRESS_KEY);
    if (inProgressToken === refToken) {
      console.log('⏳ [useTokenReferralTracking] Token validation in progress elsewhere:', refToken.substring(0, 8) + '...');
      return;
    }

    // Early exit: Currently validating in this instance
    if (isValidatingRef.current) {
      console.log('⏳ [useTokenReferralTracking] Currently validating in this instance');
      return;
    }

    // Early exit: Token has failed before (check localStorage)
    const failedTokens = getFailedTokens();
    const previousFailure = failedTokens.find(f => f.token === refToken);
    
    if (previousFailure && previousFailure.attemptCount >= MAX_RETRY_ATTEMPTS) {
      console.warn('🚫 [useTokenReferralTracking] Token previously failed validation:', {
        token: refToken.substring(0, 8) + '...',
        reason: previousFailure.reason,
        lastAttempt: previousFailure.lastAttempt,
        attemptCount: previousFailure.attemptCount
      });
      validatedTokenRef.current = refToken; // Mark as processed
      markTokenAsProcessed(refToken); // Also mark in sessionStorage
      return;
    }

    // Process the referral token
    const processReferralToken = async () => {
      // Mark token as in-progress BEFORE starting validation (prevents concurrent calls)
      sessionStorage.setItem(IN_PROGRESS_KEY, refToken);
      isValidatingRef.current = true;

      console.log('🔍 [useTokenReferralTracking] Processing token:', {
        token: refToken.substring(0, 8) + '...',
        length: refToken.length,
        type: refToken.length <= 20 ? 'short' : 'legacy',
        timestamp: new Date().toISOString()
      });

      try {
        // Validate the token (supports both long and short formats)
        const validationResult = await validateToken(refToken);
        
        if (validationResult.valid) {
          console.log('✅ [useTokenReferralTracking] Valid referral token:', {
            recruiter: validationResult.recruiter_user_id,
            job: validationResult.job_id,
            company: validationResult.company_id,
            token_type: refToken.length <= 20 ? 'short' : 'legacy'
          });

          // Calculate expiration (90 days from now)
          const now = new Date();
          const expiresAt = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));

          // Store referral data with token info
          setReferralData({
            recruiter_id: validationResult.recruiter_user_id,
            referral_source: 'secure_token',
            original_job_id: validationResult.job_id,
            company_id: validationResult.company_id,
            timestamp: now.toISOString(),
            expires_at: expiresAt.toISOString(),
            token: refToken,
            token_metadata: {
              ...validationResult.metadata,
              token_format: refToken.length <= 20 ? 'short' : 'legacy',
              validated_at: now.toISOString()
            },
          });

          // Clear any previous failure records for this token
          clearFailedToken(refToken);
          
          // Mark token as successfully processed in sessionStorage
          markTokenAsProcessed(refToken);
          
        } else {
          console.warn('⚠️ [useTokenReferralTracking] Invalid referral token:', {
            token: refToken.substring(0, 8) + '...',
            reason: validationResult.reason
          });

          // Record the failure
          recordFailedToken(refToken, validationResult.reason);

          // Clear any stale referral data for this token
          if (referralData?.token === refToken) {
            setReferralData(null);
          }
        }
      } catch (error) {
        console.error('❌ [useTokenReferralTracking] Exception:', error);
        recordFailedToken(refToken, 'exception');
      } finally {
        // Mark this token as validated (don't try again)
        validatedTokenRef.current = refToken;
        isValidatingRef.current = false;
        
        // Clear in-progress flag
        sessionStorage.removeItem(IN_PROGRESS_KEY);
      }
    };

    processReferralToken();
  }, [refToken, setReferralData, validateToken]);

  return {
    hasActiveReferral: !!referralData,
    referralData,
  };
}

// Helper functions for managing failed tokens
function getFailedTokens(): FailedTokenRecord[] {
  try {
    const stored = localStorage.getItem(FAILED_TOKENS_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function recordFailedToken(token: string, reason: string) {
  try {
    const failedTokens = getFailedTokens();
    const existing = failedTokens.find(f => f.token === token);

    if (existing) {
      existing.attemptCount += 1;
      existing.lastAttempt = new Date().toISOString();
      existing.reason = reason;
    } else {
      failedTokens.push({
        token,
        attemptCount: 1,
        lastAttempt: new Date().toISOString(),
        reason
      });
    }

    // Keep only last 50 failed tokens to prevent localStorage bloat
    const trimmed = failedTokens.slice(-50);
    localStorage.setItem(FAILED_TOKENS_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to record failed token:', error);
  }
}

function clearFailedToken(token: string) {
  try {
    const failedTokens = getFailedTokens();
    const filtered = failedTokens.filter(f => f.token !== token);
    localStorage.setItem(FAILED_TOKENS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to clear failed token:', error);
  }
}

// sessionStorage helpers for per-session deduplication
function getProcessedTokens(): string[] {
  try {
    const stored = sessionStorage.getItem(PROCESSED_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function markTokenAsProcessed(token: string) {
  try {
    const processed = getProcessedTokens();
    if (!processed.includes(token)) {
      processed.push(token);
      sessionStorage.setItem(PROCESSED_KEY, JSON.stringify(processed));
      console.log('✅ [useTokenReferralTracking] Token marked as processed:', token.substring(0, 8) + '...');
    }
  } catch (error) {
    console.error('Failed to mark token as processed:', error);
  }
}
